/**
 * @file ticketService.ts
 * @brief Central support tickets filed against a project.
 */

import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';

export type TicketStatus = 'open' | 'task_created' | 'closed' | 'cancelled';

export interface Ticket {
  id: string;
  ticketRef: string;
  projectId?: string;
  projectName?: string;
  title: string;
  description: string;
  priority: string;
  resolveBy?: string;
  status: TicketStatus;
  taskId?: string;
  taskRef?: string;
  resolution?: string;
  createdBy: string;
  createdByName?: string;
  resolvedAt?: string;
  cancelledAt?: string;
  createdAt?: string;
  updatedAt?: string;
  canManage: boolean;
  canDelete: boolean;
}

export interface TicketProjectOption {
  id: string;
  projRef?: string;
  name: string;
  status?: string;
}

export interface TicketListResult {
  items: Ticket[];
  total: number;
  page: number;
  pageSize: number;
}

function mapTicket(raw: Record<string, unknown>): Ticket {
  return {
    id: String(raw.id ?? ''),
    ticketRef: String(raw.ticketRef ?? ''),
    projectId: raw.projectId != null ? String(raw.projectId) : undefined,
    projectName: raw.projectName != null ? String(raw.projectName) : undefined,
    title: String(raw.title ?? ''),
    description: String(raw.description ?? ''),
    priority: String(raw.priority ?? 'p3'),
    resolveBy: raw.resolveBy != null ? String(raw.resolveBy).slice(0, 10) : undefined,
    status: (raw.status as TicketStatus) ?? 'open',
    taskId: raw.taskId != null ? String(raw.taskId) : undefined,
    taskRef: raw.taskRef != null ? String(raw.taskRef) : undefined,
    resolution: raw.resolution != null ? String(raw.resolution) : undefined,
    createdBy: String(raw.createdBy ?? ''),
    createdByName: raw.createdByName != null ? String(raw.createdByName) : undefined,
    resolvedAt: raw.resolvedAt != null ? String(raw.resolvedAt) : undefined,
    cancelledAt: raw.cancelledAt != null ? String(raw.cancelledAt) : undefined,
    createdAt: raw.createdAt != null ? String(raw.createdAt) : undefined,
    updatedAt: raw.updatedAt != null ? String(raw.updatedAt) : undefined,
    canManage: Boolean(raw.canManage),
    canDelete: Boolean(raw.canDelete),
  };
}

export const ticketService = {
  async list(params?: { status?: string; projectId?: string; search?: string; priority?: string; resolve?: string }): Promise<TicketListResult> {
    const response = await apiClient.get(API_ENDPOINTS.TICKETS.LIST, { params });
    const data = unwrapResponse(response) as { items?: Record<string, unknown>[]; total?: number; page?: number; pageSize?: number };
    return {
      items: Array.isArray(data?.items) ? data.items.map(mapTicket) : [],
      total: Number(data?.total ?? 0),
      page: Number(data?.page ?? 1),
      pageSize: Number(data?.pageSize ?? 50),
    };
  },

  async listProjects(): Promise<TicketProjectOption[]> {
    const response = await apiClient.get(API_ENDPOINTS.TICKETS.PROJECTS);
    const data = unwrapResponse(response);
    return Array.isArray(data)
      ? data.map((raw: Record<string, unknown>) => ({
          id: String(raw.id ?? ''),
          projRef: raw.projRef != null ? String(raw.projRef) : undefined,
          name: String(raw.name ?? ''),
          status: raw.status != null ? String(raw.status) : undefined,
        }))
      : [];
  },

  async create(body: { projectId: string; title: string; description: string; priority: string; resolveBy: string }): Promise<Ticket> {
    const response = await apiClient.post(API_ENDPOINTS.TICKETS.CREATE, body);
    return mapTicket(unwrapResponse(response) as Record<string, unknown>);
  },

  async convert(id: string, body: {
    startDate: string;
    dueDate: string;
    category: string;
    estimatedMinutes: number;
    responsibleUserId?: string;
  }): Promise<Ticket> {
    const response = await apiClient.post(API_ENDPOINTS.TICKETS.CONVERT(id), body);
    return mapTicket(unwrapResponse(response) as Record<string, unknown>);
  },

  async close(id: string, resolution: string): Promise<Ticket> {
    const response = await apiClient.post(API_ENDPOINTS.TICKETS.CLOSE(id), { resolution });
    return mapTicket(unwrapResponse(response) as Record<string, unknown>);
  },

  async cancel(id: string): Promise<Ticket> {
    const response = await apiClient.post(API_ENDPOINTS.TICKETS.CANCEL(id));
    return mapTicket(unwrapResponse(response) as Record<string, unknown>);
  },

  async remove(id: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.TICKETS.DELETE(id));
  },
};
